// Wallet cryptographic primitives: keypair generation, EVM address derivation,
// SSS split/combine, and ECDSA signing of the ERC-191 personal_sign envelope.
//
// All private-key material lives only in this module and is wiped from
// JS-visible buffers as soon as the operation finishes.

import { secp256k1 } from '@noble/curves/secp256k1';
import { keccak_256 } from '@noble/hashes/sha3';
import { split, combine } from 'shamir-secret-sharing';

export type Keypair = {
  privateKey: Uint8Array;
  publicKey: Uint8Array; // 65-byte uncompressed (0x04 ‖ X ‖ Y)
  address: string;       // 0x-prefixed lowercase hex, 20 bytes
};

export function generateKeypair(): Keypair {
  const privateKey = secp256k1.utils.randomPrivateKey();
  const publicKey = secp256k1.getPublicKey(privateKey, false); // uncompressed
  const address = addressFromPublicKey(publicKey);
  return { privateKey, publicKey, address };
}

export function addressFromPublicKey(publicKey: Uint8Array): string {
  // Drop the 0x04 prefix from the uncompressed public key, keccak256 the
  // remaining 64 bytes, take the last 20 bytes, hex-encode lowercase.
  const xy = publicKey[0] === 0x04 ? publicKey.slice(1) : publicKey;
  const hash = keccak_256(xy);
  const addrBytes = hash.slice(-20);
  return '0x' + bytesToHex(addrBytes);
}

// SSS 2-of-3: any two of the three shares can reconstruct the key. The three
// parties are:
//   shares[0] → User device (kept in iframe localStorage)
//   shares[1] → Sigil provider share (encrypted at rest with KMS)
//   shares[2] → Customer recovery share (sent S2S to the customer's webhook)
export async function splitKey(privateKey: Uint8Array, threshold = 2, total = 3): Promise<Uint8Array[]> {
  return split(privateKey, total, threshold);
}

export async function combineShares(shares: Uint8Array[]): Promise<Uint8Array> {
  return combine(shares);
}

export type SignatureHex = string; // 0x-prefixed 65-byte (r ‖ s ‖ v) hex

export function signPersonalMessage(privateKey: Uint8Array, message: string): SignatureHex {
  // EIP-191 personal_sign: hash the prefixed payload, ECDSA-secp256k1 sign.
  const prefix = `\x19Ethereum Signed Message:\n${message.length}`;
  const buf = new TextEncoder().encode(prefix + message);
  const digest = keccak_256(buf);
  const sig = secp256k1.sign(digest, privateKey);
  // sig.toCompactRawBytes() returns r||s; recovery id is sig.recovery (0|1).
  // Ethereum signatures pack v = 27 + recovery into the trailing byte.
  const compact = sig.toCompactRawBytes();
  const v = 27 + (sig.recovery ?? 0);
  const out = new Uint8Array(65);
  out.set(compact, 0);
  out[64] = v;
  return '0x' + bytesToHex(out);
}

// signUserOpHash signs an ERC-4337 userOpHash (32 bytes) the same way
// LightAccount v2 expects on its EOA path: the userOpHash is wrapped with
// "\x19Ethereum Signed Message:\n32" before keccak, and the r||s||v
// signature is returned. The host adds the LightAccount signature-type
// byte (0x00 for owner ECDSA) before forwarding to the bundler.
export function signUserOpHash(privateKey: Uint8Array, userOpHash: Uint8Array): SignatureHex {
  if (userOpHash.length !== 32) throw new Error('signUserOpHash: hash must be 32 bytes');
  const prefix = new TextEncoder().encode('\x19Ethereum Signed Message:\n32');
  const buf = new Uint8Array(prefix.length + userOpHash.length);
  buf.set(prefix, 0);
  buf.set(userOpHash, prefix.length);
  const digest = keccak_256(buf);
  const sig = secp256k1.sign(digest, privateKey);
  const compact = sig.toCompactRawBytes();
  const v = 27 + (sig.recovery ?? 0);
  const out = new Uint8Array(65);
  out.set(compact, 0);
  out[64] = v;
  return '0x' + bytesToHex(out);
}

// signDigest signs a 32-byte digest as-is (no further hashing). Used by the
// EIP-712 path where the typed-data has already been hashed elsewhere.
export function signDigest(privateKey: Uint8Array, digest: Uint8Array): SignatureHex {
  if (digest.length !== 32) throw new Error('signDigest: digest must be 32 bytes');
  const sig = secp256k1.sign(digest, privateKey);
  const compact = sig.toCompactRawBytes();
  const v = 27 + (sig.recovery ?? 0);
  const out = new Uint8Array(65);
  out.set(compact, 0);
  out[64] = v;
  return '0x' + bytesToHex(out);
}

export function bytesToHex(b: Uint8Array): string {
  const hex = '0123456789abcdef';
  let out = '';
  for (let i = 0; i < b.length; i++) {
    out += hex[b[i] >> 4];
    out += hex[b[i] & 0x0f];
  }
  return out;
}

export function hexToBytes(s: string): Uint8Array {
  const clean = s.startsWith('0x') ? s.slice(2) : s;
  const out = new Uint8Array(clean.length / 2);
  for (let i = 0; i < out.length; i++) {
    out[i] = parseInt(clean.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}

export function wipe(b: Uint8Array): void {
  b.fill(0);
}

export function base64Encode(b: Uint8Array): string {
  let bin = '';
  for (let i = 0; i < b.length; i++) bin += String.fromCharCode(b[i]);
  return btoa(bin);
}

export function base64Decode(s: string): Uint8Array {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
